import { IHeavyDutyMachineryNotificationsPage } from '../interfaces/heavyDutyMachinery/INotification';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** `before` is the id of the last IHeavyDutyMachineryNotification already loaded (omit for the first page); `limit` defaults server-side. */
export const getNotifications = async (before?: string, limit?: number): Promise<IHeavyDutyMachineryNotificationsPage> => {
  const params: Record<string, string | number> = {};
  if (before) params.before = before;
  if (limit) params.limit = limit;
  const response = await apiService<IHeavyDutyMachineryNotificationsPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load notifications');
};

/** Real delete, unlike hideConversation -- a notification has exactly one owner, nothing to protect on another participant's side. */
export const deleteNotification = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATION_DELETE(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete notification');
};

export const getUnreadNotificationCount = async (): Promise<number> => {
  const response = await apiService<number>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_UNREAD_COUNT,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load unread notification count');
};

export const markNotificationRead = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATION_READ(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to mark notification as read');
};

export const markAllNotificationsRead = async (): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_READ_ALL,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to mark all notifications as read');
};

/** Same "-all" convention as markAllNotificationsRead -- deletes every notification the user owns. */
export const deleteAllNotifications = async (): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL,
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete all notifications');
};
